/**
 * Booking Hooks
 * React Query hooks for session availability and booking
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import * as bookingService from "../services/booking.service"

export function useAvailability(ritualId: string, month: number, year: number) {
  return useQuery({
    queryKey: ["availability", ritualId, month, year],
    queryFn: () => bookingService.getAvailability(ritualId, month, year),
    enabled: !!ritualId && month > 0 && year > 0,
    staleTime: 1000 * 60 * 2,
  })
}

export function useBookSession() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      availabilityId,
      message,
      successUrl,
      cancelUrl,
    }: {
      availabilityId: string
      message?: string
      successUrl?: string
      cancelUrl?: string
    }) => bookingService.bookSession(availabilityId, message, { successUrl, cancelUrl }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["availability"] })
      queryClient.invalidateQueries({ queryKey: ["my-bookings"] })
      queryClient.invalidateQueries({ queryKey: ["user-booked-sessions"] })
    },
  })
}

export function useMyBookings(status?: string) {
  return useQuery({
    queryKey: ["my-bookings", status],
    queryFn: () => bookingService.getMyBookings(status),
    staleTime: 1000 * 60 * 5,
  })
}

export function useCancelBooking() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (bookingId: string) => bookingService.cancelBooking(bookingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["availability"] })
      queryClient.invalidateQueries({ queryKey: ["my-bookings"] })
      queryClient.invalidateQueries({ queryKey: ["user-booked-sessions"] })
    },
  })
}

export function useRequestTime() {
  return useMutation({
    mutationFn: ({ ritualId, message }: { ritualId: string; message?: string }) =>
      bookingService.requestTime(ritualId, message),
  })
}

export function useRescheduleBooking() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ bookingId, availabilityId }: { bookingId: string; availabilityId: string }) =>
      bookingService.rescheduleBooking(bookingId, availabilityId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["availability"] })
      queryClient.invalidateQueries({ queryKey: ["coaching-availability"] })
      queryClient.invalidateQueries({ queryKey: ["my-bookings"] })
      queryClient.invalidateQueries({ queryKey: ["user-booked-sessions"] })
    },
  })
}
